import {
  Alert,
  Button,
  Center,
  ChakraModal,
  ListItem,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  useDisclosure,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { COMPANY_TYPES, CompanyType } from '@bcba/types';
import { useHub } from '../../../../../config/auth/auth';
import { useUserContext } from '../../../../../contexts/UserContext';
import { HeaderImage } from './HeaderImage';

export const DigitalDossierInformativeAlert = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { justSignedIn, setJustSignedIn } = useHub();

  const companyType = user.companyType || COMPANY_TYPES.OTHER;

  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: justSignedIn,
  });

  const ownerIdentityDocument = t('documentationNeeded.ownerIdentityDocument');
  const proxies = t('documentationNeeded.proxies');
  const socialContractAndChanges = t(
    'documentationNeeded.socialContractAndChanges',
  );
  const partnersIdentityDocument = t(
    'documentationNeeded.partnersIdentityDocument',
  );
  const membershipMeetingMinutes = t(
    'documentationNeeded.membershipMeetingMinutes',
  );
  const statute = t('documentationNeeded.statute');
  const assemblyMinutes = t('documentationNeeded.assemblyMinutes');
  const boardMinutes = t('documentationNeeded.boardMinutes');
  const legalRepresentativesIdentityDocument = t(
    'documentationNeeded.legalRepresentativesIdentityDocument',
  );
  const statuteWithTransitoryProvisions = t(
    'documentationNeeded.statuteWithTransitoryProvisions',
  );
  const chiefAdministratorIdentityDocument = t(
    'documentationNeeded.chiefAdministratorIdentityDocument',
  );
  const socialContract = t('documentationNeeded.socialContract');
  const extraDocsMessage = t('documentationNeeded.extraDocsMessage');

  const mapCompanyType: Record<CompanyType, string[]> = {
    [COMPANY_TYPES.PF]: [ownerIdentityDocument, proxies],
    [COMPANY_TYPES.SRL]: [
      socialContractAndChanges,
      partnersIdentityDocument,
      membershipMeetingMinutes,
      proxies,
    ],
    [COMPANY_TYPES.SA]: [
      statute,
      assemblyMinutes,
      boardMinutes,
      legalRepresentativesIdentityDocument,
      proxies,
    ],
    [COMPANY_TYPES.COOP]: [
      statute,
      assemblyMinutes,
      boardMinutes,
      legalRepresentativesIdentityDocument,
      proxies,
    ],
    [COMPANY_TYPES.SAS]: [
      statuteWithTransitoryProvisions,
      chiefAdministratorIdentityDocument,
      proxies,
    ],
    [COMPANY_TYPES.SH]: [partnersIdentityDocument, socialContract, proxies],
    [COMPANY_TYPES.AS]: [extraDocsMessage],
    [COMPANY_TYPES.FUND]: [extraDocsMessage],
    [COMPANY_TYPES.FID]: [extraDocsMessage],
    [COMPANY_TYPES.SAU]: [extraDocsMessage],
    [COMPANY_TYPES.OTHER]: [extraDocsMessage],
  };

  const modalTextStart = t('documentationNeeded.modal.text.start').replace(
    '{{companyType}}',
    t(`companyTypes.${companyType}`),
  );
  const modalDocsMessages = mapCompanyType[companyType];
  const modalTextEnd = t('documentationNeeded.modal.text.end');

  return (
    <>
      <Alert
        variant="gray"
        title={t('documentationNeeded.alert.title')}
        learnMoreButtonLabel={t('documentationNeeded.alert.viewDetails')}
        onClickLearnMoreButton={onOpen}
      />
      {/* TODO: fix Modal component in UI */}
      <ChakraModal
        isOpen={isOpen}
        onClose={onClose}
        size={'2xl'}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Center py={2} bgColor={'background-secondary-default'} mb={6}>
              <HeaderImage />
            </Center>
            <Text size="lg" variant="medium">
              {t('documentationNeeded.modal.title')}
            </Text>
          </ModalHeader>
          <ModalBody>
            <Text whiteSpace="break-spaces" variant="regular" color="gray.600">
              {modalTextStart}
              <UnorderedList>
                {modalDocsMessages?.map((m) => <ListItem>{m}</ListItem>)}
              </UnorderedList>
              {'\n'}
              {modalTextEnd}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant={'primary'}
              size={'lg'}
              onClick={() => {
                if (justSignedIn) setJustSignedIn(false);
                onClose();
              }}
            >
              {t('documentationNeeded.modal.understood')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ChakraModal>
    </>
  );
};
